import { DEFAULT_PAGE_INFO } from '~/lib/constants'
import { getSfCollection } from '~/lib/shopify/collections/collection-by-id'
import { DEFAULT_COLLECTION_QUERY_PARAMS } from '~/lib/shopify/collections/constants'
import {
  PLP_HITS_PER_PAGE,
  SHOPIFY_FILTERS_SORT_BY,
  SHOPIFY_FILTERS_SORT_KEYS,
} from '~/lib/shopify/constants'

import {
  GID,
  sfCollection,
  type UnlikelyPageInfo,
} from '@unlikelystudio/commerce-connector'
import { useSearch } from '@unlikelystudio/react-ecommerce-hooks'

import { ProductCardProps } from '~/components/Slices/ProductCard'

import { useSearchAndDiscoveryContext } from '~/providers/SearchAndDiscoveryProvider'

import useLocale from '~/hooks/useLocale'

import { serializeCardProductContent } from '~/data/serialize-card-product'

type ShopifySearchFiltersData = {
  results: ProductCardProps[]
  pageInfo: UnlikelyPageInfo | null
}

type UseShopifyCollectionProductsParams = {
  id: GID
  initialData: ShopifySearchFiltersData
  pageInfo?: UnlikelyPageInfo | null
}

function getSfSortFromSortByParam(
  sortBy: SHOPIFY_FILTERS_SORT_KEYS,
): Pick<
  Parameters<typeof sfCollection>[1],
  'productsSortKey' | 'productReverse'
> {
  const fallBack = SHOPIFY_FILTERS_SORT_BY['default']
  return SHOPIFY_FILTERS_SORT_BY[sortBy] ?? fallBack
}

export function useShopifyCollectionProducts({
  id,
  initialData,
  pageInfo,
}: UseShopifyCollectionProductsParams) {
  const { sortBy, productFilters } = useSearchAndDiscoveryContext()

  const locale = useLocale()

  const computedPageParam = pageInfo
    ? { after: pageInfo?.endCursor }
    : undefined

  return useSearch<
    UseShopifyCollectionProductsParams['initialData'],
    unknown,
    {
      id: GID
      sortBy: typeof sortBy
      productFilters: typeof productFilters
    }
  >({
    key: `shopifyCollectionPage-${id}-${locale}`,
    additionalQueryKeyParams: { id, sortBy, productFilters },
    searchFunction: async ({
      pageParam = {},
      queryKey: [, { hitsPerPage }, { sortBy, id, productFilters }],
    }) => {
      const { productsSortKey, productReverse } =
        getSfSortFromSortByParam(sortBy)

      const [response] = await Promise.all([
        getSfCollection({
          id,
          locale,
          productsFirst: hitsPerPage,
          ...DEFAULT_COLLECTION_QUERY_PARAMS,
          ...(pageParam?.after && {
            productsAfter: pageParam.after,
          }),
          ...(pageParam?.before && {
            productsBefore: pageParam.before,
          }),
          productsFilters: productFilters ?? [],
          ...(productsSortKey && { productsSortKey }),
          ...(productReverse && { productReverse }),
        })?.catch((error) => {
          console.error(error)
          return {
            collection: {
              products: [],
              productsPageInfo: DEFAULT_PAGE_INFO,
            },
          }
        }),
      ])


      const { collection } = response ?? {}

      const { products, productsPageInfo } = collection ?? {}

      const results = products ?? []


      // Serialiaze the product cards
      const serializedProductCards = await Promise.all(
        results?.map(
          async (product) => await serializeCardProductContent(product, locale),
        ) ?? [],
      )

      return {
        pageInfo: productsPageInfo ?? DEFAULT_PAGE_INFO,
        results: serializedProductCards ?? [],
      }
    },
    paginationOptions: {
      hitsPerPage: PLP_HITS_PER_PAGE,
      getNextPageParam: (response) => {
        const { pageInfo } = response ?? {}

        return pageInfo?.hasNextPage
          ? { after: pageInfo?.endCursor }
          : undefined
      },
      getPreviousPageParam: (response) => {
        const { pageInfo } = response ?? {}

        return pageInfo?.hasPreviousPage
          ? { before: pageInfo?.startCursor }
          : undefined
      },
    },
    queryOptions: {
      keepPreviousData: true,
      refetchOnMount: true,
      initialData: initialData
        ? {
          pages: [initialData],
          pageParams: [computedPageParam],
        }
        : undefined,
    },
  })
}
